import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { pitchLabel } from "@/lib/pitchLabel";
import { pitchFormatOkForTeam } from "@/lib/refereeEligibility";
import { RefereeSelectItems } from "@/components/calendar/RefereeSelectItems";
import { FABadge, isFAImported } from "@/components/app/FABadge";
import { X, Pencil, Repeat, AlertTriangle, ArrowRight, CheckCircle2, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { RefNudgeControl, RefNudgeTag, PostMatchOutstanding } from "@/components/calendar/RefNudgeControl";

export function BookingDetailDialog({
  openId, setOpenId, detail, detailLoading,
  kitLabels, user,
  teams = [], allTeams = [], pitches = [], referees = [], refereeBusy = [],
  onCancel, onAmend, onSeriesAmend, onSeriesPreview, onSeriesCancel,
  onNudgeReferee,
}) {
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [teamClash, setTeamClash] = useState(null);

  // Series editor state — replaces the read-only view when the user hits "Edit series".
  const [seriesEditing, setSeriesEditing] = useState(false);
  const [seriesEdit, setSeriesEdit] = useState(null);
  const [seriesSaving, setSeriesSaving] = useState(false);
  // Preview state — after "Preview changes" is clicked we show a before/after list
  // BEFORE the actual PATCH goes through. `null` = still editing / no preview loaded.
  const [seriesPreview, setSeriesPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Reset editing state whenever a different booking opens
  useEffect(() => {
    setEditing(false); setEdit(null);
    setSeriesEditing(false); setSeriesEdit(null);
    setSeriesPreview(null); setPreviewLoading(false);
  }, [openId]);

  // Eligible referees + pitches for the amend form — mirror backend rules so the
  // picker only offers refs the server would accept, and pitches whose game_format
  // matches the selected team's format.
  const editTeam = useMemo(() => {
    if (!edit?.team_id) return null;
    const pool = (user?.role === "admin" && allTeams.length ? allTeams : teams);
    return pool.find(t => t.id === edit.team_id) || null;
  }, [edit?.team_id, teams, allTeams, user]);
  const eligibleReferees = referees;
  const eligiblePitches = useMemo(() => {
    const active = pitches.filter(p => p.active !== false);
    if (edit?.session_type !== "game" || !editTeam?.game_format) return active;
    return active.filter(p => pitchFormatOkForTeam(p, editTeam));
  }, [pitches, edit?.session_type, editTeam]);

  const openEdit = () => {
    if (!detail) return;
    setEdit({
      booking_date: detail.booking_date,
      start_hour: detail.start_hour, start_min: detail.start_min || 0,
      end_hour: detail.end_hour, end_min: detail.end_min || 0,
      pitch_id: detail.pitch_id || "",
      team_id: detail.team_id || "",
      age_group: detail.age_group || "",
      // book_mode toggles between a specific team vs a year-group-only training.
      // Only meaningful for training sessions — auto-detected from the current booking.
      book_mode: detail.session_type === "training" && !detail.team_id && detail.age_group ? "yeargroup" : "team",
      session_type: detail.session_type || "training",
      match_type: detail.match_type || "",
      referee_id: detail.referee_id || "",
      referee_actual_name: detail.referee_actual_name || "",
      shared_team_ids: Array.isArray(detail.shared_team_ids) ? [...detail.shared_team_ids] : [],
      referee_tbd: false,
      opponent: detail.opponent || "",
      notes: detail.notes || "",
    });
    setEditing(true);
  };

  const saveEdit = async (override = false) => {
    if (!detail || !edit) return;
    // Build a full patch — the backend BookingUpdateIn accepts any subset.
    const yearGroupMode = edit.session_type === "training" && edit.book_mode === "yeargroup";
    if (yearGroupMode && !edit.age_group) { toast.error("Pick an age group"); return; }
    if (!yearGroupMode && !edit.team_id) { toast.error("Pick a team"); return; }
    if (!edit.pitch_id) { toast.error("Pick a pitch"); return; }
    const isGame = edit.session_type === "game";
    if (isGame && !edit.match_type) { toast.error("Pick a match type for the game"); return; }
    if (isGame && (edit.match_type === "league" || edit.match_type === "cup") && !edit.referee_id && !edit.referee_tbd) {
      toast.error("Referee is required for league/cup matches (or tick 'Referee TBD')"); return;
    }
    const patch = {
      booking_date: edit.booking_date,
      start_hour: edit.start_hour, start_min: edit.start_min || 0,
      end_hour: edit.end_hour, end_min: edit.end_min || 0,
      pitch_id: edit.pitch_id,
      team_id: yearGroupMode ? null : edit.team_id,
      age_group: yearGroupMode ? edit.age_group : null,
      notes: edit.notes || null,
      session_type: edit.session_type,
      match_type: isGame ? edit.match_type : null,
      referee_id: isGame && !edit.referee_tbd ? (edit.referee_id || null) : null,
      referee_tbd: isGame ? !!edit.referee_tbd : false,
      referee_actual_name: isGame ? (edit.referee_actual_name || "") : "",
      ...(user?.role === "admin" ? { shared_team_ids: edit.shared_team_ids || [] } : {}),
      opponent: isGame ? (edit.opponent || null) : null,
    };
    setSaving(true);
    setTeamClash(null);
    try {
      await onAmend(detail.id, { ...patch, ...(override ? { admin_override: true } : {}) });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.team_clash) setTeamClash({ detail: d, patch });
      else toast.error(formatApiError(d) || "Amend failed");
    } finally { setSaving(false); }
  };

  const openSeriesEdit = () => {
    if (!detail) return;
    setSeriesEdit({
      opponent: detail.opponent || "",
      notes: detail.notes || "",
      shared_team_ids: Array.isArray(detail.shared_team_ids) ? [...detail.shared_team_ids] : [],
      // Time / date-shift fields default to the current booking's slot so
      // "no change" ships as an idempotent patch (backend allows partial time
      // updates as long as ALL four fields are set together, or none).
      start_hour: detail.start_hour,
      start_min: detail.start_min || 0,
      end_hour: detail.end_hour,
      end_min: detail.end_min || 0,
      shift_days: 0,
    });
    setSeriesEditing(true);
  };

  // Build the PATCH payload from seriesEdit — shared by preview + save.
  // Only include fields the user actually changed so the preview's "Also updating"
  // line is accurate and the PATCH doesn't touch untouched values.
  const buildSeriesPatch = () => {
    if (!detail || !seriesEdit) return null;
    const patch = {};
    const origOpponent = detail.opponent || "";
    const origNotes = detail.notes || "";
    const origShared = Array.isArray(detail.shared_team_ids) ? [...detail.shared_team_ids].sort() : [];
    const curShared = [...(seriesEdit.shared_team_ids || [])].sort();
    if (detail.session_type === "game" && (seriesEdit.opponent || "") !== origOpponent) {
      patch.opponent = seriesEdit.opponent;
    }
    if ((seriesEdit.notes || "") !== origNotes) {
      patch.notes = seriesEdit.notes;
    }
    if (detail.session_type === "training" && JSON.stringify(origShared) !== JSON.stringify(curShared)) {
      patch.shared_team_ids = seriesEdit.shared_team_ids;
    }
    const timesChanged =
      seriesEdit.start_hour !== detail.start_hour ||
      (seriesEdit.start_min || 0) !== (detail.start_min || 0) ||
      seriesEdit.end_hour !== detail.end_hour ||
      (seriesEdit.end_min || 0) !== (detail.end_min || 0);
    if (timesChanged) {
      patch.start_hour = seriesEdit.start_hour;
      patch.start_min = seriesEdit.start_min || 0;
      patch.end_hour = seriesEdit.end_hour;
      patch.end_min = seriesEdit.end_min || 0;
    }
    if (seriesEdit.shift_days) patch.shift_days = seriesEdit.shift_days;
    return patch;
  };

  const previewSeries = async () => {
    if (!detail?.recurring_series_id || !onSeriesPreview) return;
    const patch = buildSeriesPatch();
    if (!patch || Object.keys(patch).length === 0) {
      // Nothing to preview — mirror the backend's "Nothing to update" rejection.
      window.alert("Change at least one field before previewing.");
      return;
    }
    setPreviewLoading(true);
    try {
      const preview = await onSeriesPreview(detail.recurring_series_id, patch);
      if (preview) setSeriesPreview(preview);
    } finally { setPreviewLoading(false); }
  };

  const saveSeries = async () => {
    if (!detail || !seriesEdit || !detail.recurring_series_id) return;
    setSeriesSaving(true);
    try {
      await onSeriesAmend(detail.recurring_series_id, buildSeriesPatch());
    } finally { setSeriesSaving(false); }
  };

  const cancelSeries = async () => {
    if (!detail?.recurring_series_id) return;
    if (!window.confirm("Cancel every remaining booking in this series? Past sessions stay untouched.")) return;
    setSeriesSaving(true);
    try {
      await onSeriesCancel?.(detail.recurring_series_id);
    } finally { setSeriesSaving(false); }
  };

  // `detail.can_manage` is computed server-side against the CURRENT team ownership
  // (booker, admin, YGL for the age group, team owner, or additional coach) so a
  // newly-assigned lead coach gets Amend + Cancel + full field access — including
  // adding a referee — on bookings the previous owner made. Falls back to the old
  // booker/admin check for older responses that don't include the flag.
  const canModify = detail && user && detail.status === "confirmed" && (
    detail.can_manage === true
    || (detail.can_manage === undefined && (user.role === "admin" || detail.user_id === user.id))
  );
  const inSeries = !!detail?.recurring_series_id;
  const isLeagueAssigned = (refId) => /league\s*assigned/i.test((referees.find(r => r.id === refId) || {}).name || "");
  // Shared-team picker eligibility — training only, exclude the booking's own team.
  const sharedTeamOptions = (allTeams.length ? allTeams : teams).filter(t => t.id !== detail?.team_id);

  return (
    <>
      <Dialog open={!!openId} onOpenChange={(v)=>{ if (!v) setOpenId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">
            {seriesPreview ? "Preview series changes" : seriesEditing ? "Edit recurring series" : editing ? "Amend booking" : "Booking detail"}
          </DialogTitle></DialogHeader>
          {detailLoading || !detail ? (
            <div className="opacity-60 font-display tracking-widest py-8 text-center">LOADING…</div>
          ) : seriesPreview ? (
            <div className="space-y-4 text-sm" data-testid="series-preview-panel">
              {seriesPreview.has_clashes ? (
                <div className="p-3 rounded border border-red-500/50 bg-red-500/10 text-xs flex items-start gap-2" data-testid="series-preview-clash-banner">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-red-300 font-semibold">Clashes detected — fix these before saving</div>
                    <div className="opacity-80 mt-0.5">One or more moved sessions land on a slot that&apos;s already booked. Change the times/date shift, or move the conflicting bookings first.</div>
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded border border-[#83B68B]/40 bg-[#83B68B]/5 text-xs flex items-start gap-2" data-testid="series-preview-ok-banner">
                  <CheckCircle2 className="w-4 h-4 text-[#83B68B] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[#83B68B] font-semibold">Ready to save — {seriesPreview.count} future session{seriesPreview.count === 1 ? "" : "s"} will change</div>
                    <div className="opacity-70 mt-0.5">Review the new dates and times below before confirming.</div>
                  </div>
                </div>
              )}
              <div className="rounded border border-border overflow-hidden">
                <div className="max-h-72 overflow-y-auto divide-y divide-white/5" data-testid="series-preview-list">
                  {seriesPreview.changes.map((c) => {
                    const anyChange = c.date_changed || c.time_changed;
                    return (
                      <div key={c.booking_id} className={`p-2.5 text-xs flex items-center gap-2 ${c.clash ? "bg-red-500/5" : ""}`} data-testid={`series-preview-row-${c.booking_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="opacity-70">{format(parseISO(c.old_date), "EEE d MMM")} · {c.old_start_time}–{c.old_end_time}</span>
                            {anyChange && (
                              <>
                                <ArrowRight className="w-3 h-3 opacity-40" />
                                <span className="text-[#83B68B] font-semibold">
                                  {format(parseISO(c.new_date), "EEE d MMM")} · {c.new_start_time}–{c.new_end_time}
                                </span>
                              </>
                            )}
                            {!anyChange && <span className="text-[10px] uppercase tracking-widest opacity-40">no date/time change</span>}
                          </div>
                          <div className="opacity-50 mt-0.5 truncate">{c.pitch_name}{c.team_name ? ` · ${c.team_name}` : ""}</div>
                          {c.clash && (
                            <div className="text-red-300 mt-1 text-[11px]" data-testid={`series-preview-clash-${c.booking_id}`}>
                              Clash — {c.clash.who} already on {c.clash.pitch_name} at {c.clash.start_time}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {seriesPreview.fields_changing.length > 0 && (
                <div className="text-[10px] tracking-widest uppercase opacity-60" data-testid="series-preview-fields">
                  Also updating: {seriesPreview.fields_changing.filter(f => f !== "date" && f !== "time").join(", ") || "—"}
                </div>
              )}
              <div className="flex justify-end gap-2 dialog-actions">
                <button type="button" onClick={() => setSeriesPreview(null)} data-testid="series-preview-back-btn" className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border opacity-70 hover:opacity-100">Back to edit</button>
                <button
                  type="button"
                  onClick={saveSeries}
                  disabled={seriesSaving || seriesPreview.has_clashes}
                  data-testid="series-preview-confirm-btn"
                  className="btn-volt px-5 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40"
                >
                  {seriesSaving ? "Saving…" : `Confirm & save ${seriesPreview.count} change${seriesPreview.count === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          ) : seriesEditing && seriesEdit ? (
            <div className="space-y-4 text-sm" data-testid="booking-series-edit-form">
              <div className="p-3 rounded border border-[#83B68B]/40 bg-[#83B68B]/5 text-xs opacity-90">
                Changes apply to every **future** confirmed booking in this series. Past sessions stay untouched.
              </div>
              {detail.session_type === "game" && (
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Opponent</Label>
                  <Input value={seriesEdit.opponent} onChange={e=>setSeriesEdit({...seriesEdit, opponent: e.target.value})} data-testid="series-edit-opponent" className="mt-1.5" />
                </div>
              )}
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Notes</Label>
                <Textarea rows={2} value={seriesEdit.notes} onChange={e=>setSeriesEdit({...seriesEdit, notes: e.target.value})} data-testid="series-edit-notes" className="mt-1.5" />
              </div>

              {/* Time change — applies to every future occurrence */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Start (every session)</Label>
                  <div className="flex gap-1.5 mt-1.5">
                    <Input type="number" min={0} max={23} value={seriesEdit.start_hour} onChange={e=>setSeriesEdit({...seriesEdit, start_hour: parseInt(e.target.value||"0")})} data-testid="series-edit-start-hour" />
                    <Input type="number" min={0} max={45} step={15} value={seriesEdit.start_min} onChange={e=>setSeriesEdit({...seriesEdit, start_min: parseInt(e.target.value||"0")})} data-testid="series-edit-start-min" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">End (every session)</Label>
                  <div className="flex gap-1.5 mt-1.5">
                    <Input type="number" min={1} max={24} value={seriesEdit.end_hour} onChange={e=>setSeriesEdit({...seriesEdit, end_hour: parseInt(e.target.value||"0")})} data-testid="series-edit-end-hour" />
                    <Input type="number" min={0} max={45} step={15} value={seriesEdit.end_min} onChange={e=>setSeriesEdit({...seriesEdit, end_min: parseInt(e.target.value||"0")})} data-testid="series-edit-end-min" />
                  </div>
                </div>
              </div>

              {/* Date shift — move every future session forward/back */}
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Shift every future session by (days)</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input
                    type="number" min={-90} max={90}
                    value={seriesEdit.shift_days}
                    onChange={e=>setSeriesEdit({...seriesEdit, shift_days: parseInt(e.target.value||"0")})}
                    data-testid="series-edit-shift-days"
                    className="max-w-[120px]"
                  />
                  <div className="text-xs opacity-60">
                    e.g. <span className="text-[#83B68B] font-medium">7</span> pushes each session forward a week, <span className="text-[#83B68B] font-medium">-1</span> moves them a day earlier.
                  </div>
                </div>
              </div>

              {detail.session_type === "training" && (
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Sharing pitch with (up to 3 other teams)</Label>
                  <div className="mt-1.5 space-y-1.5 max-h-52 overflow-y-auto pr-1">
                    {sharedTeamOptions.length === 0 && <div className="text-xs opacity-50">No other teams to pick from.</div>}
                    {sharedTeamOptions.map(t => {
                      const on = seriesEdit.shared_team_ids.includes(t.id);
                      const atCap = !on && seriesEdit.shared_team_ids.length >= 3;
                      return (
                        <label key={t.id} className={`flex items-center gap-2 cursor-pointer text-xs ${atCap ? "opacity-40 cursor-not-allowed" : ""}`}>
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={atCap}
                            onChange={(e) => setSeriesEdit(cur => ({
                              ...cur,
                              shared_team_ids: e.target.checked
                                ? [...cur.shared_team_ids, t.id]
                                : cur.shared_team_ids.filter(x => x !== t.id),
                            }))}
                            data-testid={`series-edit-share-${t.id}`}
                            className="w-4 h-4 accent-[#83B68B]"
                          />
                          <span>{t.name} · {t.age_group}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="text-[10px] opacity-50 mt-1">{seriesEdit.shared_team_ids.length}/3 selected</div>
                </div>
              )}
              <div className="flex flex-wrap justify-between gap-2 pt-1">
                {onSeriesCancel && (
                  <button
                    type="button"
                    onClick={cancelSeries}
                    disabled={seriesSaving}
                    data-testid="series-edit-cancel-all-btn"
                    className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-red-500/60 text-red-400 hover:bg-red-500/10 disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    <X className="w-3 h-3" aria-hidden /> Cancel entire series
                  </button>
                )}
                <div className="flex gap-2 ml-auto">
                  <button type="button" onClick={()=>setSeriesEditing(false)} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border opacity-70 hover:opacity-100">Back</button>
                  <button type="button" onClick={previewSeries} disabled={previewLoading || seriesSaving} data-testid="series-edit-preview-btn" className="btn-volt px-5 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40">
                    {previewLoading ? "Loading…" : "Preview changes"}
                  </button>
                </div>
              </div>{/* /series-edit-actions */}
            </div>
          ) : editing && edit ? (
            <div className="space-y-4 text-sm" data-testid="booking-edit-form">
              {/* Pitch + date row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Pitch / Venue</Label>
                  <Select value={edit.pitch_id} onValueChange={v=>setEdit({...edit, pitch_id: v})}>
                    <SelectTrigger data-testid="edit-pitch" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {eligiblePitches.length === 0 && <div className="px-3 py-2 text-xs opacity-50">No pitches match this team's format.</div>}
                      {eligiblePitches.map(p => {
                        // Prefer the admin-set venue short_code; fall back to an auto-acronym
                        // derived from the venue name (or a parenthetical like "(JFAN)").
                        const loc = p.location || p.venue_name || "";
                        const paren = loc.match(/\(([^)]+)\)/);
                        const auto = paren
                          ? paren[1].toUpperCase().slice(0, 4)
                          : loc.split(/\s+/).map(w => w[0] || "").join("").toUpperCase().slice(0, 3);
                        const acronym = p.venue_short_code || auto;
                        return (
                          <SelectItem key={p.id} value={p.id}>
                            {acronym ? `[${acronym}] ` : ""}{p.name}{p.venue_name ? ` · ${p.venue_name}` : ""}{p.game_format ? ` · ${p.game_format}` : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Date</Label>
                  <Input type="date" value={edit.booking_date} onChange={e=>setEdit({...edit, booking_date: e.target.value})} data-testid="edit-date" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Start</Label>
                  <div className="flex gap-1.5 mt-1.5">
                    <Input type="number" min={0} max={23} value={edit.start_hour} onChange={e=>setEdit({...edit, start_hour: parseInt(e.target.value||"0")})} data-testid="edit-start-hour" />
                    <Input type="number" min={0} max={45} step={15} value={edit.start_min} onChange={e=>setEdit({...edit, start_min: parseInt(e.target.value||"0")})} data-testid="edit-start-min" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">End</Label>
                  <div className="flex gap-1.5 mt-1.5">
                    <Input type="number" min={1} max={24} value={edit.end_hour} onChange={e=>setEdit({...edit, end_hour: parseInt(e.target.value||"0")})} data-testid="edit-end-hour" />
                    <Input type="number" min={0} max={45} step={15} value={edit.end_min} onChange={e=>setEdit({...edit, end_min: parseInt(e.target.value||"0")})} data-testid="edit-end-min" />
                  </div>
                </div>
              </div>

              {/* Session type toggle — switch between training and game in-place */}
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Session type</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                  {[{v:"training",l:"Training"},{v:"game",l:"Game"}].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={()=>setEdit({...edit, session_type: o.v, match_type: o.v === "game" ? (edit.match_type || "league") : "" })}
                      data-testid={`edit-session-${o.v}`}
                      className={`py-2 rounded text-xs font-display tracking-widest uppercase ${edit.session_type===o.v ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}
                    >{o.l}</button>
                  ))}
                </div>
              </div>

              {/* Team / Age group picker with toggle for training */}
              <div>
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">
                    {edit.session_type === "training" && edit.book_mode === "yeargroup" ? "Age group" : "Team"}
                  </Label>
                  {edit.session_type === "training" && (
                    <div className="inline-flex items-center rounded-full border border-border overflow-hidden text-[10px] tracking-widest uppercase">
                      <button type="button" onClick={()=>setEdit({...edit, book_mode: "team"})} data-testid="edit-mode-team"
                        className={`px-2.5 py-1 transition-colors ${edit.book_mode !== "yeargroup" ? "bg-[#83B68B] text-white" : "opacity-60 hover:opacity-100"}`}>Team</button>
                      <button type="button" onClick={()=>setEdit({...edit, book_mode: "yeargroup"})} data-testid="edit-mode-yeargroup"
                        className={`px-2.5 py-1 transition-colors ${edit.book_mode === "yeargroup" ? "bg-[#83B68B] text-white" : "opacity-60 hover:opacity-100"}`}>Age group</button>
                    </div>
                  )}
                </div>
                {edit.session_type === "training" && edit.book_mode === "yeargroup" ? (
                  <Select value={edit.age_group} onValueChange={v=>setEdit({...edit, age_group: v})}>
                    <SelectTrigger data-testid="edit-age-group" className="mt-1.5"><SelectValue placeholder="Pick an age group" /></SelectTrigger>
                    <SelectContent>
                      {["U6","U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","Adults"].map(a => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={edit.team_id} onValueChange={v=>setEdit({...edit, team_id: v})}>
                    <SelectTrigger data-testid="edit-team" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(user?.role === "admin" && allTeams.length ? allTeams : teams).filter(t => t && t.id).map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name} · {t.age_group}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Game-only fields: match type, opponent, referee */}
              {edit.session_type === "game" && (
                <>
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase opacity-70">Match type</Label>
                    <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                      {[{v:"league",l:"League"},{v:"cup",l:"Cup"},{v:"friendly",l:"Friendly"}].map(o => (
                        <button
                          key={o.v}
                          type="button"
                          onClick={()=>setEdit({...edit, match_type: o.v})}
                          data-testid={`edit-match-${o.v}`}
                          className={`py-2 rounded text-xs font-display tracking-widest uppercase ${edit.match_type===o.v ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}
                        >{o.l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase opacity-70">Opponent <span className="opacity-50">(optional)</span></Label>
                    <Input value={edit.opponent} onChange={e=>setEdit({...edit, opponent: e.target.value})} data-testid="edit-opponent" className="mt-1.5" />
                  </div>
                  {(edit.match_type === "league" || edit.match_type === "cup") && (
                    <div>
                      <Label className="text-[10px] tracking-widest uppercase opacity-70">Referee <span className="text-[#83B68B]">(required)</span></Label>
                      <Select value={edit.referee_id || ""} onValueChange={v=>setEdit({...edit, referee_id: v})} disabled={!!edit.referee_tbd}>
                        <SelectTrigger data-testid="edit-referee" className="mt-1.5"><SelectValue placeholder={edit.referee_tbd ? "Referee to be allocated" : "Select referee"} /></SelectTrigger>
                        <SelectContent>
                          <RefereeSelectItems referees={eligibleReferees} busy={refereeBusy} emptyText="No referees set up yet." date={edit.booking_date} isAdmin={user?.role === "admin"} team={editTeam}
                            ignoreBookingId={detail?.id}
                            start={edit.start_hour * 60 + (edit.start_min || 0)} end={edit.end_hour * 60 + (edit.end_min || 0)} />
                        </SelectContent>
                      </Select>
                      <label className="mt-2 flex items-center gap-2 text-[11px] opacity-80 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid="edit-referee-tbd"
                          checked={!!edit.referee_tbd}
                          onChange={e=>setEdit({...edit, referee_tbd: e.target.checked, referee_id: e.target.checked ? "" : edit.referee_id})}
                          className="accent-[#83B68B] w-3.5 h-3.5"
                        />
                        <span>Referee TBD — amend without a ref, I'll assign later</span>
                      </label>
                      {isLeagueAssigned(edit.referee_id) && (
                        <div className="mt-2">
                          <Label className="text-[10px] tracking-widest uppercase opacity-70">Referee's actual name <span className="opacity-50">(league-assigned — add when known)</span></Label>
                          <Input value={edit.referee_actual_name || ""} onChange={e=>setEdit({...edit, referee_actual_name: e.target.value})} placeholder="e.g. Jo Bloggs" data-testid="edit-referee-actual-name" className="mt-1.5" />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {user?.role === "admin" && (
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Sharing pitch with <span className="opacity-50">(admin · up to 3 other teams)</span></Label>
                  <div className="mt-1.5 grid sm:grid-cols-2 gap-x-4 gap-y-1 max-h-40 overflow-y-auto pr-1" data-testid="edit-shared-teams">
                    {sharedTeamOptions.filter(t => t.id !== edit.team_id).map(t => {
                      const on = (edit.shared_team_ids || []).includes(t.id);
                      const atCap = !on && (edit.shared_team_ids || []).length >= 3;
                      return (
                        <label key={t.id} className={`flex items-center gap-2 text-xs cursor-pointer ${atCap ? "opacity-40" : ""}`}>
                          <input type="checkbox" checked={on} disabled={atCap} className="accent-[#83B68B]" data-testid={`edit-shared-${t.id}`}
                            onChange={() => setEdit({ ...edit, shared_team_ids: on ? edit.shared_team_ids.filter(x => x !== t.id) : [...(edit.shared_team_ids || []), t.id] })} />
                          <span>{t.age_group ? `${t.age_group} · ` : ""}{t.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Notes</Label>
                <Textarea rows={2} value={edit.notes} onChange={e=>setEdit({...edit, notes: e.target.value})} data-testid="edit-notes" className="mt-1.5" />
              </div>
              {teamClash && (
                <div className="rounded border border-amber-500/60 bg-amber-500/10 p-3 space-y-2" data-testid="amend-team-clash-panel">
                  <div className="text-[10px] tracking-widest uppercase text-amber-300">Team already booked that day</div>
                  <div className="text-sm">{formatApiError(teamClash.detail)}</div>
                  <div className="text-xs opacity-70">Admin override: move this booking anyway and leave the other one in place.</div>
                  <button type="button" onClick={() => saveEdit(true)} disabled={saving} data-testid="amend-admin-override-btn" className="text-[10px] tracking-widest uppercase px-3 py-2 rounded bg-amber-500 text-black font-bold disabled:opacity-40">
                    {saving ? "Saving…" : "Move anyway"}
                  </button>
                </div>
              )}
              <div className="flex justify-end gap-2 dialog-actions">
                <button type="button" onClick={()=>setEditing(false)} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border opacity-70 hover:opacity-100">Cancel</button>
                <button type="button" onClick={() => saveEdit(false)} disabled={saving} data-testid="edit-save-btn" className="btn-volt px-5 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40">
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-sm" data-testid="booking-detail-modal">
              {inSeries && (
                <div className="p-3 rounded border border-amber-400/40 bg-amber-500/5 text-xs flex items-start gap-2" data-testid="series-warning-banner">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-amber-300 font-semibold">This is part of a recurring series</div>
                    <div className="opacity-80 mt-0.5">Any Amend or Cancel here changes ONLY this date. To change every future session at once, use <strong>Edit series</strong>.</div>
                    {canModify && onSeriesAmend && (
                      <button
                        type="button"
                        onClick={openSeriesEdit}
                        data-testid="detail-edit-series-btn"
                        className="mt-2 text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-amber-400/60 text-amber-200 hover:bg-amber-500/10 inline-flex items-center gap-1.5"
                      >
                        <Repeat className="w-3 h-3" /> Edit series
                      </button>
                    )}
                  </div>
                </div>
              )}
              {detail.fa_change_pending && (
                <div className="p-3 rounded border border-sky-400/50 bg-sky-500/10 flex items-start gap-2" data-testid="fa-change-pending-banner">
                  <AlertTriangle className="w-4 h-4 text-sky-300 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sky-200 font-semibold text-sm">FA Full-Time has moved this fixture</div>
                    <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                      <span className="opacity-70">{format(parseISO(detail.booking_date), "EEE d MMM")} · {String(detail.start_hour).padStart(2,"0")}:{String(detail.start_min || 0).padStart(2,"0")}</span>
                      <ArrowRight className="w-3 h-3 opacity-40" />
                      <span className="text-[#83B68B] font-semibold" data-testid="fa-change-new-slot">{format(parseISO(detail.fa_change_pending.new_date), "EEE d MMM")} · {detail.fa_change_pending.new_kickoff}</span>
                    </div>
                    <div className="opacity-70 text-xs mt-1">Your booking hasn't been moved yet. Confirm to move it to the new date/kick-off on the same pitch, or keep the current booking.</div>
                    {canModify && (
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          data-testid="fa-change-confirm-btn"
                          onClick={async () => {
                            try {
                              const { default: api } = await import("@/lib/api");
                              await api.post(`/bookings/${detail.id}/fa-change/confirm`);
                              toast.success("Booking moved to the new FA date/time");
                              setOpenId(null);
                            } catch (err) {
                              const d = err.response?.data?.detail;
                              toast.error(typeof d === "string" ? d : (d?.message || "Slot not free — amend the booking manually"));
                            }
                          }}
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-3 py-1.5 rounded bg-[#83B68B] text-white hover:bg-[#6ba173]"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Confirm change
                        </button>
                        <button
                          type="button"
                          data-testid="fa-change-dismiss-btn"
                          onClick={async () => {
                            try {
                              const { default: api } = await import("@/lib/api");
                              await api.post(`/bookings/${detail.id}/fa-change/dismiss`);
                              toast.success("Kept current booking");
                              setOpenId(null);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Could not update");
                            }
                          }}
                          className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border opacity-80 hover:opacity-100"
                        >
                          Keep current booking
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {detail.confirmation_status === "awaiting" && (
                <div className="p-3 rounded border border-amber-400/50 bg-amber-500/10 flex items-start gap-2" data-testid="awaiting-confirmation-banner">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-amber-300 font-semibold text-sm">Awaiting external confirmation</div>
                    <div className="opacity-80 text-xs mt-1">
                      This venue is booked in our calendar, but you still need to confirm the slot with the venue directly (school office / hall keeper). Once they've confirmed back, mark it below so everyone knows it's locked in.
                    </div>
                    {canModify && (
                      <button
                        type="button"
                        data-testid="mark-booking-confirmed-btn"
                        onClick={async () => {
                          try {
                            const { default: api } = await import("@/lib/api");
                            await api.post(`/bookings/${detail.id}/mark-confirmed`);
                            toast.success("Booking marked as confirmed");
                            setOpenId(null);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Could not confirm");
                          }
                        }}
                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-3 py-1.5 rounded bg-[#83B68B] text-white hover:bg-[#6ba173]"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Booking confirmed
                      </button>
                    )}
                  </div>
                </div>
              )}
              {detail.confirmation_status === "confirmed" && (
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] inline-flex items-center gap-1.5" data-testid="confirmation-confirmed-chip">
                  <CheckCircle2 className="w-3 h-3" /> Venue-confirmed
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="font-display font-bold text-xl">{pitchLabel(detail)}</div>
                  <div className="text-xs opacity-60">{format(parseISO(detail.booking_date), "EEEE, d MMMM yyyy")}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-black text-2xl text-[#83B68B]">
                    {String(detail.start_hour).padStart(2,"0")}:{String(detail.start_min || 0).padStart(2,"0")} — {String(detail.end_hour).padStart(2,"0")}:{String(detail.end_min || 0).padStart(2,"0")}
                  </div>
                  <div className="text-[10px] tracking-widest uppercase opacity-60 inline-flex items-center gap-2 justify-end w-full">
                    <span>{detail.session_type}{detail.match_type ? " · " + detail.match_type : ""}</span>
                    {isFAImported(detail) && <FABadge />}
                  </div>
                </div>
              </div>

              {(detail.team_name || detail.age_group) && (
                <div className="p-3 rounded border border-border bg-muted">
                  <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1">{Array.isArray(detail.shared_team_names) && detail.shared_team_names.length > 0 ? "Primary team" : "Team"}</div>
                  <div className="font-medium">{detail.team_name || "—"} {detail.age_group ? `· ${detail.age_group}` : ""}</div>
                  {detail.team_color && <div className="text-xs opacity-70 mt-0.5" data-testid="detail-kit-label">Kit: {kitLabels[detail.team_color] || detail.team_color}{(kitLabels[detail.team_color] && kitLabels[detail.team_color] !== detail.team_color) ? ` (${detail.team_color})` : ""}</div>}
                  {detail.opponent && <div className="text-xs opacity-70 mt-0.5">vs {detail.opponent}</div>}
                  {detail.league?.name && <div className="text-xs opacity-70 mt-0.5">League: {detail.league.name}</div>}
                  {Array.isArray(detail.shared_team_names) && detail.shared_team_names.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/5" data-testid="detail-shared-teams">
                      <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1">Sharing this pitch with</div>
                      <div className="flex flex-wrap gap-1.5">
                        {detail.shared_team_names.map((n, i) => (
                          <span key={`${n}-${i}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-[#83B68B]/10 border border-[#83B68B]/40 text-[#83B68B]">{n}</span>
                        ))}
                      </div>
                      <div className="text-[10px] opacity-60 mt-1.5">Every coach on these teams can see this booking under My Bookings.</div>
                    </div>
                  )}
                </div>
              )}

              {detail.coach && (
                <div className="p-3 rounded border border-border bg-muted">
                  <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1">Coach</div>
                  <div className="font-medium">{detail.coach.name || "—"}</div>
                  <div className="text-xs opacity-70 mt-0.5 flex flex-wrap gap-x-3">
                    {detail.coach.email && <a href={`mailto:${detail.coach.email}`} className="hover:text-[#83B68B]">✉ {detail.coach.email}</a>}
                    {detail.coach.phone && <span>📞 {detail.coach.phone}</span>}
                  </div>
                </div>
              )}

              {detail.referee && (
                <div className="p-3 rounded border border-border bg-muted">
                  <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1 flex items-center justify-between">
                    <span>Referee {detail.referee.is_qualified ? "· Qualified" : "· Unqualified"}</span>
                    {detail.expected_referee_fee != null && <span className="text-[#83B68B]">£{Number(detail.expected_referee_fee).toFixed(2)}</span>}
                  </div>
                  <div className="font-medium">{detail.referee.name} {detail.referee.level ? `· ${detail.referee.level}` : ""}</div>
                  <div className="text-xs opacity-70 mt-0.5 flex flex-wrap gap-x-3">
                    {detail.referee.email && <a href={`mailto:${detail.referee.email}`} className="hover:text-[#83B68B]">✉ {detail.referee.email}</a>}
                    {detail.referee.phone && <span>📞 {detail.referee.phone}</span>}
                  </div>
                </div>
              )}

              {/* Referee TBD banner — admins can nudge the booking coach */}
              {!detail.referee && detail.session_type === "game" && (detail.match_type === "league" || detail.match_type === "cup") && (
                <div className="p-3 rounded border border-amber-400/40 bg-amber-500/5 text-xs flex items-start gap-2" data-testid="ref-tbd-banner">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-amber-300 font-semibold">Referee still to be allocated</div>
                    <div className="opacity-80 mt-0.5">This {detail.match_type} game doesn't have a referee assigned yet.</div>
                    {user?.role === "admin" && onNudgeReferee && (
                      <button
                        type="button"
                        onClick={() => onNudgeReferee(detail.id)}
                        data-testid="detail-nudge-ref-btn"
                        className="mt-2 text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-amber-400/60 text-amber-200 hover:bg-amber-500/10 inline-flex items-center gap-1.5"
                      >
                        Nudge coach to assign a referee
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="text-xs opacity-70 flex items-center gap-2 flex-wrap">Booked by <strong>{detail.user_name}</strong> ({detail.user_email}) {user?.role !== "admin" && <RefNudgeTag ov={detail.ref_nudge} />}</div>
              <PostMatchOutstanding missing={detail.post_match_outstanding} />
              {user?.role === "admin" && detail.session_type === "game" && (detail.match_type === "league" || detail.match_type === "cup") && detail.status === "confirmed" && (
                <RefNudgeControl booking={detail} onSaved={ov => { detail.ref_nudge = ov; }} />
              )}
              {detail.notes && <div className="text-xs opacity-70 italic">"{detail.notes}"</div>}

              {detail.session_type === "game" && user?.role !== "vendor" && (
                <div className="flex justify-start">
                  <Link to={`/app/incidents?from_booking=${detail.id}`} data-testid="detail-report-cwo"
                    className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 inline-flex items-center gap-1.5">
                    <ShieldAlert className="w-3 h-3" /> Report to CWO
                  </Link>
                </div>
              )}

              {canModify && (
                <div className="flex justify-end gap-2 dialog-actions">
                  <button
                    type="button"
                    onClick={() => onCancel && onCancel(detail.id)}
                    data-testid="detail-cancel-btn"
                    className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-red-400 hover:text-red-400 inline-flex items-center gap-1.5"
                  >
                    Cancel booking
                  </button>
                  <button
                    type="button"
                    onClick={openEdit}
                    data-testid="detail-amend-btn"
                    className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5"
                  >
                    <Pencil className="w-3 h-3" /> Amend
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
